import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import esTranslations from './translations/es.json';
import enTranslations from './translations/en.json';
import deTranslations from './translations/de.json';

export type Language = 'es' | 'en' | 'de';

type TranslationValue = string | Record<string, unknown>;
type Translations = Record<string, TranslationValue>;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Translations> = {
  es: esTranslations as Translations,
  en: enTranslations as Translations,
  de: deTranslations as Translations,
};

const STORAGE_KEY = 'rankaro_language';

function getInitialLanguage(): Language {
  // 1. Check localStorage
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'es' || saved === 'en' || saved === 'de') return saved;
    
    // 2. Detect from browser
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('de')) return 'de';
    if (browserLang.startsWith('es')) return 'es';
  }
  
  // 3. Default to English for global audience
  return 'en';
}

function getNestedValue(obj: Translations, path: string): string {
  const keys = path.split('.');
  let current: unknown = obj;
  
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = (current as Record<string, unknown>)[key];
    } else {
      return path; // Return key if not found
    }
  }
  
  return typeof current === 'string' ? current : path;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguage = useCallback(async (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
    // Update document lang attribute for SEO
    document.documentElement.lang = lang;
    
    // Sync language preference to profile in database
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('profiles')
          .update({ preferred_language: lang } as Record<string, unknown>)
          .eq('id', user.id);
      }
    } catch {
      // Silently fail - user might not be logged in
    }
  }, []);

  // Set initial document lang
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback((key: string): string => {
    return getNestedValue(translations[language], key);
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    // Fallback for HMR edge cases - resolve translations directly instead of returning raw keys
    const fallbackLang = getInitialLanguage();
    return {
      language: fallbackLang,
      setLanguage: () => {},
      t: (key: string) => getNestedValue(translations[fallbackLang], key),
    };
  }
  return context;
};
